import { ICON } from '@core/util/icon';
import { AccountingExportTypes } from '@models/businessSettings/settings';
import { MatterFileType } from '@models/matter';
import { Selector } from '@ngxs/store';
import { UrlResult, UrlService } from '@services/url/url.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { NavigationState } from '@state/navigation/navigation.state';
import { DocumentNavigationState } from '@state/user-preferences/user-preferences.model';
import { BusinessFeatureFlagSelectors, BusinessState } from '@state/business';
import { AccountingExportSettings } from '@models/businessSettings/settings';
import { UserPreferencesState } from '@state/user-preferences/user-preferences.state';
import { Waze } from './side-nav.utils';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { MatterMortgage } from '@models/mortgage';
import { MatterFormDataState } from '@state/matter/form-data';
import { MatterFormData } from '@models/matter-form-data';
import { MatterDocumentFlows } from '@models/documents';
import { GenericFormsTemplatesState } from '@state/generic-forms';
import { GlobalGenericFormTemplate } from '@models/global-forms';
import { EregXmlSelectors } from 'src/app/shared/state/global/ereg-xml';

export interface MenuLink {
	label: string;
	links?: MenuLink[];
	icon?: ICON;
	id?: number;
	url?: string;
	show?: boolean;
	selected?: boolean;
	expanded?: boolean;
}
export class VM_SideNavSelector {
	@Selector([
		SelectedMatter.Province,
		SelectedMatter.id,
		SelectedMatter.matterFileType,
		NavigationState.currentUrl,
		BusinessState.getAccountingExportSettings,
		MatterMortgagesState.getVTBMortgage,
		MatterFormDataState.getAllFormData,
		SelectedMatter.getEnabledDocumentFlow,
		BusinessFeatureFlagSelectors.isGenericFormsEnabled,
		GenericFormsTemplatesState.getGenericFormByMatterType,
		EregXmlSelectors.isTPR,
	])
	static MenuLinks(
		province: string,
		matterId: string,
		fileType: MatterFileType,
		currentUrl: string,
		accountingExportSettings: AccountingExportSettings,
		vtbMortgage: MatterMortgage,
		formData: MatterFormData[],
		enabledDocumentFlows: MatterDocumentFlows,
		isGenericFormsEnabled: boolean,
		getGenericFormByMatterType: GlobalGenericFormTemplate[],
		isTPR: boolean,
	): MenuLink[] {
		let menuLinks = [];

		if (!matterId) {
			return menuLinks;
		}

		switch (province) {
			default:
				menuLinks = VM_SideNavSelector.BuildOntarioSideNav(
					matterId,
					fileType,
					accountingExportSettings,
					vtbMortgage,
					formData,
					enabledDocumentFlows,
					isTPR,
					isGenericFormsEnabled &&
						!!getGenericFormByMatterType.length &&
						!formData.length,
				);
				break;
		}

		const isExact = menuLinks.some((x) =>
			x?.links?.some((link) => link.url === currentUrl),
		);

		return menuLinks?.map((menuLink) => {
			menuLink?.links?.forEach((link: { url: string }) => {
				if (currentUrl.includes(link.url) && !isExact) {
					menuLink.selected = true;
				}

				if (currentUrl === link.url && isExact) {
					menuLink.selected = true;
				}
			});

			return menuLink;
		});
	}

	@Selector([
		VM_SideNavSelector.MenuLinks,
		UserPreferencesState.getDocumentNavigationState,
		SelectedMatter.id,
	])
	static SelectedGroups(
		menuLinks: MenuLink[],
		docNavState: (string) => DocumentNavigationState,
		matterId: string,
	): { [key: string]: boolean } {
		const updatedGroups = { ...docNavState(matterId) };
		menuLinks.forEach((link) => {
			if (link.links) {
				if (updatedGroups[link.id]) {
					link.expanded = true;
				}
				if (!Object.prototype.hasOwnProperty.call(updatedGroups, link.id))
					updatedGroups[link.id] = false;
			}
		});
		return updatedGroups;
	}

	static BuildOntarioSideNav = (
		matterId: string,
		fileType: MatterFileType,
		accountingExportSettings: AccountingExportSettings,
		vtbMortgage: MatterMortgage,
		formData: MatterFormData[],
		enabledDocumentFlows: MatterDocumentFlows,
		isTPR: boolean,
		isGenericFormsEnabled: boolean,
	): MenuLink[] => {
		const isPurchase = fileType === MatterFileType.PURCHASE;
		const isSale = fileType === MatterFileType.SALE;
		const isRefinance = fileType === MatterFileType.REFINANCE;

		const isVtbMortgage = isSale && !!vtbMortgage;

		const wordDocumentsLabel = 'Documents';
		let htmlDocumentsLabel = 'Documents';

		if (enabledDocumentFlows === MatterDocumentFlows.HTML_WORD) {
			htmlDocumentsLabel = 'Documents (old)';
		}

		return [
			new Waze('Overview', true)
				.withIcon(ICON.OVERVIEW)
				.withPath(this.getMatterOverviewUrl(matterId)),
			new Waze('Parties', !isRefinance)
				.withIcon(ICON.PARTIES)
				.withId(0)
				.withChildren([
					new Waze('Clients', true).withPath(
						this.getMatterPartiesClientsUrl(matterId),
					),
					new Waze(`${isSale ? 'Purchasers' : 'Vendors'}`, true).withPath(
						this.getMatterPartiesOtherPartiesUrl(matterId),
					),
				]),

			new Waze('Parties', isRefinance)
				.withIcon(ICON.PARTIES)
				.withPath(this.getMatterPartiesMortgagorsUrl(matterId)),

			new Waze('Property', true)
				.withIcon(ICON.PROPERTY)
				.withId(1)
				.withChildren([
					new Waze('APS', !isRefinance).withPath(
						this.getMatterAPSUrl(matterId),
					),
					new Waze('Property details', true).withPath(
						this.getMatterPropertyDetails(matterId),
					),
					new Waze('Title search', true).withPath(
						this.getMatterTitleSearchUrl(matterId),
					),
					new Waze('Execution affidavits', true).withPath(
						this.getMatterExecutionAffidavitsUrl(matterId),
					),
					new Waze('LTT & Considerations', isPurchase).withPath(
						this.getMatterLttUrl(matterId),
					),
					new Waze(
						'Fire insurance',
						isPurchase || isRefinance || isVtbMortgage,
					).withPath(this.getfireInsruanceUrl(matterId)),
					new Waze('Requisitions', isPurchase).withPath(
						this.getmatterRequisitionUrl(matterId),
					),
					new Waze('Jurisdiction', true).withPath(
						this.getMatterJurisdictionsUrl(matterId),
					),
				]),

			new Waze('Encumbrances', isSale || isRefinance)
				.withIcon(ICON.MORTGAGE)
				.withPath(this.getMatterEncumberancesUrl(matterId)),
			new Waze(
				`${isVtbMortgage ? 'VTB Mortgage' : 'Mortgages'}`,
				isVtbMortgage || isPurchase || isRefinance,
			)
				.withIcon(ICON.MORTGAGE)
				.withPath(this.getMatterMortgagesUrl(matterId)),
			new Waze('Title insurance', !isSale)
				.withPath(this.getMatterTitleInsuranceUrl(matterId))
				.withIcon(ICON.SECURITY),
			new Waze('Finances', true)
				.withId(2)
				.withIcon(ICON.DOLLAR)
				.withChildren([
					new Waze('Commissions', !isRefinance).withPath(
						this.getmatterCommissionUrl(matterId),
					),
					new Waze('Adjustments', !isRefinance).withPath(
						this.getmatterAdjustmentsUrl(matterId),
					),
					new Waze('Account', true).withPath(
						this.getmatterAccountsUrl(matterId),
					),
					new Waze('Bills or debts', true).withPath(
						this.getmatterDebtsUrl(matterId),
					),
					new Waze('Redirection', false).withPath(
						this.getMatterRedirectionUrl(matterId),
					),
					new Waze('Trust ledger', true).withPath(
						this.getmatterLedgerUrl(matterId),
					),
				]),

			new Waze(
				`${isPurchase ? 'Purchase' : isSale ? 'Sale' : 'Refinance'} documents`,
				true,
			)
				.withIcon(ICON.DOCUMENT)
				.withId(3)
				.withChildren([
					new Waze(
						wordDocumentsLabel,
						enabledDocumentFlows === MatterDocumentFlows.WORD ||
							enabledDocumentFlows === MatterDocumentFlows.LEAP ||
							enabledDocumentFlows === MatterDocumentFlows.HTML_WORD,
					).withPath(this.getmatterPackagesUrl(matterId)),
					new Waze(
						htmlDocumentsLabel,
						enabledDocumentFlows === MatterDocumentFlows.HTML ||
							enabledDocumentFlows === MatterDocumentFlows.HTML_WORD,
					).withPath(this.getmatterDocumentsUrl(matterId)),
					new Waze('Uploaded', true).withPath(
						this.getmatterUploadedDocsUrl(matterId),
					),
					new Waze('Forms', isGenericFormsEnabled).withPath(
						this.getmatterUploadedGenericFormsUrl(matterId),
					),
					new Waze(
						'Forms',
						!isGenericFormsEnabled &&
							(!isRefinance || (isRefinance && formData.length > 0)),
					).withPath(this.getmatterUploadedFormsUrl(matterId)),
					new Waze('Resp. to requisitions', isSale).withPath(
						this.getmatterResponseToRequisitionUrl(matterId),
					),
					new Waze('Undertakings given', isSale || isRefinance).withPath(
						this.getUndertakingsGivenUrl(matterId),
					),
				]),

			new Waze('Closing', true)
				.withIcon(ICON.CHECK)
				.withChildren([
					new Waze('E-Reg Charge', !isSale || isVtbMortgage).withPath(
						this.getMatterClosingChargesUrl(matterId),
					),
					new Waze(
						isTPR ? 'E-Reg Transfer (TPR)' : 'E-Reg Transfer',
						!isRefinance,
					).withPath(this.getMatterClosingTransferUrl(matterId)),
					new Waze(
						'Accounting',
						!!accountingExportSettings.accountingPlatform &&
							accountingExportSettings.accountingPlatform !==
								AccountingExportTypes.QUICKBOOKS,
					).withPath(this.getmatterAccountingCosmolex(matterId)),
				])
				.withId(4),
		];
	};

	private static getmatterDocxUrl(matterId: string): UrlResult {
		return UrlService.Matter_Documents_Docx(matterId);
	}

	private static getmatterDocumentsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Documents_SaleDocs_Overview(matterId);
	}

	private static getmatterPackagesUrl(matterId: string): UrlResult {
		return UrlService.Matter_Documents_Packages(matterId);
	}

	private static getMatterDocumentsEnvelopesUrl(matterId: string): UrlResult {
		return UrlService.Matter_Documents_Envelopes(matterId);
	}

	private static getfireInsruanceUrl(matterId: string): UrlResult {
		return UrlService.Matter_Property_FireInsurance(matterId);
	}

	private static getmatterRequisitionUrl(matterId: string): UrlResult {
		return UrlService.Matter_Documents_Requisitions(matterId);
	}

	private static getmatterResponseToRequisitionUrl(
		matterId: string,
	): UrlResult {
		return UrlService.Matter_Documents_Response_To_Requisitions(matterId);
	}

	private static getmatterERegistrationUrl(matterId: string): UrlResult {
		return UrlService.Matter_Closing_ERegistration_Transfer(matterId);
	}

	private static getMatterClosingChargesUrl(matterId: string): UrlResult {
		return UrlService.Matter_Closing_Charges(matterId);
	}

	private static getMatterClosingTransferUrl(matterId: string): UrlResult {
		return UrlService.Matter_Closing_Transfer(matterId);
	}

	private static getmatterAccountingCosmolex(matterId: string): UrlResult {
		return UrlService.Matter_Closing_Accounting_Cosmolex(matterId);
	}

	private static getmatterUploadedDocsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Documents_Uploaded(matterId);
	}

	private static getmatterUploadedGenericFormsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Documents_Generic_Forms(matterId);
	}

	private static getmatterUploadedFormsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Documents_Forms(matterId);
	}

	private static getmatterLedgerUrl(matterId: string): UrlResult {
		return UrlService.Matter_Ledger(matterId);
	}

	private static getmatterCommissionUrl(matterId: string): UrlResult {
		return UrlService.Matter_Commission(matterId);
	}

	private static getmatterAdjustmentsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Adjustments(matterId);
	}

	private static getmatterAccountsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Accounts(matterId);
	}

	private static getmatterDebtsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Debts(matterId);
	}

	private static getMatterPropertyDetails(matterId: string): UrlResult {
		return UrlService.Matter_Property(matterId);
	}

	private static getMatterOverviewUrl(matterId: string): UrlResult {
		return UrlService.Matter_Overview(matterId);
	}

	private static getMatterRedirectionUrl(matterId: string): UrlResult {
		return UrlService.Matter_Redirection(matterId);
	}

	private static getMatterAPSUrl(matterId: string): UrlResult {
		return UrlService.Matter_APS(matterId);
	}

	private static getMatterTitleSearchUrl(matterId: string): UrlResult {
		return UrlService.Matter_TitleSearch(matterId);
	}

	private static getMatterTitleInsuranceUrl(matterId: string): UrlResult {
		return UrlService.Matter_TitleInsurance(matterId);
	}

	private static getMatterPartiesClientsUrl(matterId: string): UrlResult {
		return UrlService.matterPartiesClients(matterId);
	}

	private static getMatterPartiesMortgagorsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Parties_Mortgagors(matterId);
	}

	private static getMatterPartiesOtherPartiesUrl(matterId: string) {
		return UrlService.Matter_Parties_OtherParties(matterId);
	}

	private static getMatterMortgagesUrl(matterId: string): UrlResult {
		return UrlService.Matter_Mortgages(matterId);
	}

	private static getMatterEncumberancesUrl(matterId: string): UrlResult {
		return UrlService.Matter_Encumbrances(matterId);
	}

	private static getMatterExecutionAffidavitsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Execution_Affidavits(matterId);
	}

	private static getMatterLttUrl(matterId: string): UrlResult {
		return UrlService.Matter_Ltt(matterId);
	}

	private static getUndertakingsGivenUrl(matterId: string): UrlResult {
		return UrlService.Undertakings_Given(matterId);
	}

	private static getMatterJurisdictionsUrl(matterId: string): UrlResult {
		return UrlService.Matter_Property_Jurisdictions(matterId);
	}
}
